import { Module, VuexModule, Mutation, Action } from "vuex-module-decorators";
import Store from "../../index";
import _ from "lodash";

import {
  endDateFromDynamicRange,
  formatDynamicDate,
  getFileName,
  getOrderLocation,
  isDynamicDateRange,
  startDateFromDynamicRange
} from "@/utils";
import { api, rawApi, addUrlParameter } from "@/lib";
import router from "../../../router";
import * as modals from "../../../lib/modals";
import notifications from "@/lib/notifications";
import {
  AddOrderCommodityRequest,
  AddOrderCommodityResponse,
  EditOrderCommodityRequest,
  EditOrderCommodityResponse,
  CommodityResource,
  RemoveOrderCommodityRequest,
  EditOrderCommodityUnitsRequest,
  EditOrderCommodityUnitsResponse
} from "@/models/api/orders/commodities";
import {
  OrderAccessorialResource,
  SaveAccessorialRequest,
  SaveAccessorialResponse,
  DeleteAccessorialRequest
} from "@/models/api/orders/accessorials";
import {
  OrderShipsWithSuggestionsResponse,
  AddOrderToOrderShipsWithResponse,
  AddOrderToOrderShipsWithRequest,
  ResetShipsWithOrderResponse
} from "@/models/api/orders/shipsWith";
import {
  CustomerNotReadyRequest,
  CustomerNotReadyResponse
} from "@/models/api/orders/customerNotReady";
import {
  GetOrdersRequest,
  GetOrdersResponse,
  OrderListItemResource,
  OrderResource,
  OrderParty
} from "@/models/api/orders/get";
import moment from "moment";
import {
  CreateNonCdsOrderRequest,
  CreateNonCdsOrderResponse
} from "@/models/api/nonCdsOrders/create";
import {
  UpdateNonCdsOrderRequest,
  UpdateNonCdsOrderResponse
} from "@/models/api/nonCdsOrders/update";
import { NonCdsOrderResource } from "@/models/api/nonCdsOrders/get";
import { NonCdsOrderCommodityResource } from "@/models/api/nonCdsOrders/getCommodities";
import {
  AddNonCdsOrderCommodityRequest,
  AddNonCdsOrderCommodityResponse
} from "@/models/api/nonCdsOrders/addCommodity";
import {
  UpdateNonCdsOrderCommodityResponse,
  UpdateNonCdsOrderCommodityRequest
} from "@/models/api/nonCdsOrders/updateCommodity";
import { RemoveNonCdsOrderCommodityRequest } from "@/models/api/nonCdsOrders/removeCommodity";
import { GetTruckOrderFreightDetailsRequest } from "@/models/api/trucks/orderFreight";
import {
  OrderImageResource,
  AddOrderImageRequest,
  AddOrderImageResource
} from "@/models/api/orders/images";
import {
  GetBulkOrderTimeframesRequest,
  BulkOrderTimeframeResource
} from "@/models/api/orders/getBulkTimeframes";
import {
  SetBulkOrderTimeframesRequest,
  SetBulkOrderTimeframeResult
} from "@/models/api/orders/setBulkTimeframes";
import {
  SetBulkOrderWantsAsapRequest,
  SetBulkOrderWantsAsapResult
} from "@/models/api/orders/setBulkOrdersWantsAsap";
import {
  PreviewCompleteResource,
  AllowFutureOrdersResponse,
  CompleteOrderRequest,
  CompleteOrderResponse
} from "@/models/api/orders/complete";
import { RemoveConsigneeContactFromOrderRequest, SendAccessCodeToContact } from "@/models/api/orders/consigneeContacts";
import { AlertTypeResource } from "@/models/api/system/alerts";
import {
  GetBarcodeTransactionsRequest,
  BarcodeTransactionResource,
  BarCodeDetails
} from "@/models/api/orders/barcodeTransactions";
import { ORDER_FILTER_FIELDS } from "@/consts";
import {
  PreviewScheduleOrderRequest,
  PreviewScheduleOrderResource,
  PreviewReceiveOrderResource,
  PreviewReceiveOrderRequest,
  DeliveryProfileResource
} from "@/models/api/orders/schedule";
import { DeliveryExportRequest, DeliveryExportFormat } from "@/models/api/orders/deliveryExport";
import { CompletedOrderExportRequest } from "@/models/api/orders/completedOrderExport";
import { defaultOrder, defaultOrderFields, name } from "./consts";
import {
  IOrderStore,
  IOrderResource,
  IOrderDataItem,
  IOrderSearchResponse,
  ICommodity,
  IOrderCommodity,
  IOrderNote,
  IOrderTypeToDeliveryTypeMapping
} from "@/store/types";
import { OrderRemarksRequest } from "@/models/api/orders/orderRemarks";
import { OrderDriverNoteRequest } from "@/models/api/orders/orderDriverNote";
import { AddOrderPalletRequest } from "../../../models/api/orders/orderPallet";
import { OrderTypeToDeliveryTypeMappingModule, AccountModule } from "@/store/modules";
import { getModule } from "vuex-module-decorators";

import SystemModule from "@/store/modules/system";
import AddressVerificationStatusVue from "@/components/order/AddressVerificationStatus.vue";
const systemModule = getModule(SystemModule);

if (Store.state[name]) {
  Store.unregisterModule(name);
}

function buildOrderSavePayload(currentOrder: any) {
  return {
    orderType: currentOrder.orderType,
    billToCompanyId: currentOrder.billTo.id,
    crn: currentOrder.crn,
    deliveryType: currentOrder.deliveryType,
    facility: currentOrder.facility,
    manufacturer: currentOrder.manufacturer,
    codPending: currentOrder.codPending,
    rateBy: currentOrder.rateBy,
    intermediateConsignee: currentOrder.intermediateConsignee,
    remarks: currentOrder.remarks,
    specialRequests: currentOrder.specialRequests,
    shipDate: currentOrder.shipDate,
    anticipatedReceiveDate: currentOrder.anticipatedReceiveDate,
    requestedDeliveryDate: currentOrder.requestedDeliveryDate,
    dockSearchDate: currentOrder.dockSearchDate,
    carrier: currentOrder.carrier,
    driverNote: currentOrder.driverNote,
    isAsapDelivery: currentOrder.isAsapDelivery,
    addressVerificationStatusId: currentOrder.addressVerificationStatusId
  };
}

function normalizeOrderResource(order: any): IOrderResource {
  if (!order) return order;

  return {
    ...order,
    trackingNumber: order.trackingNumber ?? order.TrackingNumber ?? null,
    trackingNumberUrl: order.trackingNumberUrl ?? order.TrackingNumberUrl ?? null
  };
}

@Module({ name: name, dynamic: true, store: Store, namespaced: true, stateFactory: true })
export default class OrderModule extends VuexModule implements IOrderStore {
  public renderAdvancedSearchResults?: boolean = false;
  public advancedSearchFilter?: any = null;
  public alertTypes: Array<string> = [];
  public uncompletedOrderAlerts: number = 0;
  public orderStatuses: Array<string> = ["AVL", "CAN", "CMP", "DSP", "PLN", "STD"];
  public currentOrder: IOrderResource = defaultOrder;
  public currentOrderPreview: IOrderResource = defaultOrder;
  public currentOrderPreviewCommodities: Array<any> = [];
  public currentOrderPreviewOrderItems: Array<any> = [];
  public currentOrderConsigneeContacts: Array<Object> = [];
  public currentOrderNotes: Array<IOrderNote> = [];
  public currentOrderReferenceNumbers: any = [];
  public currentOrderBarcodeDetails: any = { barcodeItems: [] };
  public currentOrderBarcodeLocationDetails: any = { barcodeItemLocations: [] };
  public currentOrderShipsWithOrders: Array<any> = [];
  public currentOrderCommodities: Array<any> = [];
  public currentOrderThumbnails: any = [];
  public currentOrderImages: Array<Object> = [];
  public currentDeliveryProfile: any = [];
  public currentOrderImageIndex: number;
  public loading: boolean = false;
  public pageData: any = {
    totalPages: 0,
    totalItems: 0,
    currentPage: 1,
    pageSize: 30
  };
  public dataSortKey: string = "headerNumber";
  public dataSortAscending: boolean = false;
  public data: Array<IOrderDataItem> = [];
  public noteLoading: boolean = false;
  public error: boolean = false;
  public hideManifestedOrder: boolean = false;
  public showOnlyScheduledOrders: boolean = false;
  public showOnlyIncorrectTruck: boolean = false;
  public errorMessage: string = "";
  public orderTypes: Array<Object> = [
    { name: "DEL", label: "Delivery" },
    { name: "RET", label: "Pickup/Return" },
    { name: "WILL", label: "Will Call" }
  ];
  public userFields: Array<Object> = null;
  public orderFields = defaultOrderFields;
  public searchResults: any;
  public quickSearchType: string;
  public quickSearchText: string;

  get getUserOrderFields() {
    let availableFields = this.getAvailableOrderFields;
    let fieldsToDisplay = availableFields;

    if (this.userFields)
      fieldsToDisplay = this.userFields.map(x => availableFields.filter(y => y.key === x)[0]);

    return fieldsToDisplay;
  }

  get getCurrentOrderCommodities() {
    return this.currentOrderCommodities;
  }

  get getAvailableOrderFields() {
    let fieldsToDisplay = this.orderFields;

    return fieldsToDisplay.filter(
      x =>
        (!x.requiresPermission ||
          (x.requiresPermission &&
            this.context.rootState.account.permissions.indexOf(x.requiresPermission) > -1)) &&
        !x.hidden
    );
  }

  get selectedOrders() {
    return this.data
      .filter(x => x.selected)
      .map((x: any) => ({
        selected: x.selected,
        number: x.headerNumber,
        headerNumber: x.headerNumber,
        crn: x.crn,
        scheduledDeliveryDate: x.scheduledDeliveryDate,
        truckName: x.truckName,
        zip3: x.consignee.address.zip.substring(0, 3),
        zip: x.consignee.address.zip,
        facilityCode: x.facility,
        billTo: { id: x.billTo ? x.billTo.id : null },
        consignee: x.consignee,
        shipper: x.shipper,
        timeframeStart: x.timeframeStart,
        timeframeEnd: x.timeframeEnd,
        cubes: x.cubes,
        isReceived: x.isReceived ? x.isReceived : x.received,
        location: getOrderLocation(x),
        orderType: x.orderType,
        remarks: x.remarks,
        driverNote: x.driverNote,
        isOnHold: x.isOnHold
      }));
  }

  set selectedOrders(newVal) {
    this.selectedOrders = newVal;
  }

  get selectedOrderCount() {
    return this.selectedOrders.length;
  }

  get isCarrierEnabled() : boolean {
    return !(this.currentOrder.orderType === "WILL" && this.isCdsOwnedFacility);
  }

  get totalQuantityForOrderCommodities() {
    return this.currentOrderCommodities
      ? this.currentOrderCommodities.map(commodity => commodity.count).reduce((x, y) => x + y, 0)
      : 0;
  }

  get totalChargedQuantityForOrderCommodities() {
    return this.currentOrderCommodities
      ? this.currentOrderCommodities
          .map(commodity => commodity.chargeQuantity)
          .reduce((x, y) => x + y, 0)
      : 0;
  }

  get totalUomForOrderCommodities() {
    return this.currentOrderCommodities && this.currentOrderCommodities.length > 0
      ? this.currentOrderCommodities[0].countUnit
      : "";
  }

  get totalVolumeForOrderCommodities() {
    return this.currentOrderCommodities
      ? this.currentOrderCommodities.map(commodity => commodity.volume).reduce((x, y) => x + y, 0)
      : 0;
  }

  get totalVolumeUomForOrderCommodities() {
    return this.currentOrderCommodities && this.currentOrderCommodities.length > 0
      ? this.currentOrderCommodities[0].volumeUnit
      : "";
  }

  get totalWeightForOrderCommodities() {
    return this.currentOrderCommodities
      ? this.currentOrderCommodities.map(commodity => commodity.weight).reduce((x, y) => x + y, 0)
      : 0;
  }

  get isCdsOwnedFacility() {
    var facilities = systemModule.cdsFacilities;
    if (!facilities) return false;
    return facilities.includes(this.currentOrder.facility);
  }

  totalWeightUomForOrderCommodities() {
    this.currentOrderCommodities && this.currentOrderCommodities.length > 0
      ? this.currentOrderCommodities[0].weightUnit
      : "";
  }

  @Action
  enforceHomeCarrier() {
    if(this.currentOrder.orderType === "WILL" && this.isCdsOwnedFacility && this.currentOrder.carrier !== "HOUSE") {
      this.updateCarrier("HOUSE");
    }
  }

  @Mutation
  setError(ex: any) {
    this.error = true;
    this.errorMessage = ex.msg;
  }

  @Mutation
  setLoading(isLoading: boolean) {
    this.loading = isLoading;
  }

  @Mutation
  setOrders(response: IOrderSearchResponse) {
    response.data.forEach(function(element) {
      element.selected = false;
    });
    this.pageData.totalItems = response.headers.totalitems;
    this.pageData.totalPages = response.headers.totalpages;
    if (this.data.length) {
      this.data = this.data.concat(response.data);
    } else {
      this.data = response.data;
    }
    this.loading = false;
  }

  @Mutation
  setOrderHeaders(headers) {
    this.pageData.totalItems = headers.totalitems;
    this.pageData.totalPages = headers.totalpages;
  }

  @Mutation
  getOrderPending() {
    this.loading = true;
    this.error = false;
  }

  @Mutation
  setCurrentOrder(data) {
    this.loading = false;
    this.currentOrder = normalizeOrderResource(data);
  }

  @Mutation
  setCurrentOrderPreview(data) {
    this.currentOrderPreview = data ? data : null;
  }

  @Mutation
  setOrderPreviewCommodities(data) {
    this.currentOrderPreviewCommodities = data ? data.data : null;
  }

  @Mutation
  setOrderPreviewOrderItems(data){
    this.currentOrderPreviewOrderItems = data ? data.data : [];
  }

  @Mutation
  setCurrentDeliveryProfile(data) {
    this.currentDeliveryProfile = data ? data : null;
  }

  @Mutation
  getOrderFailure(error) {
    this.loading = false;
    this.error = true;
    this.errorMessage = error.msg;
  }

  @Mutation
  createOrderPending() {
    this.loading = false;
    this.error = false;
  }

  @Mutation
  createOrderSuccess(response) {
    this.loading = false;
    this.currentOrder = normalizeOrderResource(response.data);
  }

  @Mutation
  createOrderFailure(error) {
    this.loading = false;
    this.error = true;
    this.errorMessage = error.msg;
  }

  @Mutation
  updateOrderPending() {
    this.loading = true;
    this.error = false;
  }

  @Mutation
  updateOrderSuccess(response) {
    this.loading = false;
    this.currentOrder = normalizeOrderResource(response.data);
  }

  @Mutation
  updateOrderFailure(error) {
    this.loading = false;
    this.error = true;
    this.errorMessage = error.msg;
  }

  @Mutation
  deleteOrderPending() {
    this.loading = true;
    this.error = false;
  }

  @Mutation
  deleteOrderSuccess(response) {
    this.loading = false;
    this.currentOrder = normalizeOrderResource(response.data);
  }

  @Mutation
  deleteOrderFailure(error) {
    this.loading = false;
    this.error = true;
    this.errorMessage = error.msg;
  }

  @Mutation
  receiveOrderPending() {
    this.loading = true;
    this.error = false;
  }

  @Mutation
  receiveOrderSuccess(response) {
    this.loading = false;
    this.currentOrder = normalizeOrderResource(response.data);
  }

  @Mutation
  receiveOrderFailure(error) {
    this.loading = false;
    this.error = true;
    this.errorMessage = error.msg;
  }

  @Mutation
  unscheduleOrderPending() {
    this.loading = true;
    this.error = false;
  }

  @Mutation
  unscheduleOrderSuccess(response) {
    this.loading = false;
    this.currentOrder = normalizeOrderResource(response.data);
  }

  @Mutation
  unscheduleOrderFailure(error) {
    this.loading = false;
    this.error = true;
    this.errorMessage = error.msg;
  }

  @Mutation
  orderToTruckPending() {
    this.loading = true;
    this.error = false;
  }

  @Mutation
  orderToTruckSuccess(response) {
    this.loading = false;
    this.currentOrder = normalizeOrderResource(response.data);
  }

  @Mutation
  orderToTruckFailure(error) {
    this.loading = false;
    this.error = true;
    this.errorMessage = error.msg;
  }

  @Mutation
  orderTimeframePending() {
    this.loading = true;
    this.error = false;
  }

  @Mutation
  orderTimeframeSuccess(response) {
    this.loading = false;
    this.currentOrder = normalizeOrderResource(response.data);
  }

  @Mutation
  orderTimeframeFailure(error) {
    this.loading = false;
    this.error = true;
    this.errorMessage = error.msg;
  }

  @Mutation
  cancelOrderPending() {
    this.loading = true;
    this.error = false;
  }

  @Mutation
  cancelOrderSuccess(response) {
    this.loading = false;
    this.currentOrder = normalizeOrderResource(response.data);
  }

  @Mutation
  cancelOrderFailure(error) {
    this.loading = false;
    this.error = true;
    this.errorMessage = error.msg;
  }

  @Mutation
  completeOrderPending() {
    this.loading = true;
    this.error = false;
  }

  @Mutation
  completeOrderSuccess(response) {
    this.loading = false;
    this.currentOrder = normalizeOrderResource(response.data);
  }

  @Mutation
  completeOrderFailure(error) {
    this.loading = false;
    this.error = true;
    this.errorMessage = error.msg;
  }

  @Mutation
  consolidateOrderPending() {
    this.loading = true;
    this.error = false;
  }

  @Mutation
  consolidateOrderSuccess(response) {
    this.loading = false;
    this.currentOrder = normalizeOrderResource(response.data);
  }

  @Mutation
  consolidateOrderFailure(error) {
    this.loading = false;
    this.error = true;
    this.errorMessage = error.msg;
  }

  @Mutation
  getOrderNotesPending() {
    this.noteLoading = true;
    this.error = false;
  }

  @Mutation
  getOrderNotesSuccess(response) {
    this.noteLoading = false;
    this.currentOrderNotes = response.data;
  }

  @Mutation
  getOrderNotesFailure(error) {
    this.noteLoading = false;
    this.error = true;
    this.errorMessage = error.msg;
  }

  @Mutation
  getOrderNotePending() {
    this.loading = true;
    this.error = false;
  }

  @Mutation
  getOrderNoteSuccess(response) {
    this.loading = false;
    this.currentOrder = normalizeOrderResource(response.data);
  }

  @Mutation
  getOrderNoteFailure(error) {
    this.loading = false;
    this.error = true;
    this.errorMessage = error.msg;
  }

  @Mutation
  createOrderNotePending() {
    this.loading = true;
    this.error = false;
  }

  @Mutation
  createOrderNoteSuccess(response) {
    this.loading = false;
    this.currentOrder = normalizeOrderResource(response.data);
  }

  @Mutation
  createOrderNoteFailure(error) {
    this.loading = false;
    this.error = true;
    this.errorMessage = error.msg;
  }

  @Mutation
  updateOrderNotePending() {
    this.loading = true;
    this.error = false;
  }

  @Mutation
  updateOrderNoteSuccess(response) {
    this.loading = false;
    this.currentOrder = normalizeOrderResource(response.data);
  }

  @Mutation
  updateOrderNoteFailure(error) {
    this.loading = false;
    this.error = true;
    this.errorMessage = error.msg;
  }

  @Mutation
  deleteOrderNotePending() {
    this.loading = true;
    this.error = false;
  }

  @Mutation
  deleteOrderNoteSuccess(response) {
    this.loading = false;
    this.currentOrder = normalizeOrderResource(response.data);
  }

  @Mutation
  deleteOrderNoteFailure(error) {
    this.loading = false;
    this.error = true;
    this.errorMessage = error.msg;
  }

  @Mutation
  setOrderFields(fields) {
    this.orderFields = fields;
  }

  @Mutation
  setOrderField(orderFieldChange) {
    this.orderFields[orderFieldChange.idx].hidden = !this.orderFields[orderFieldChange.idx].hidden;
  }

  @Mutation
  resetOrders() {
    this.loading = true;
    this.pageData.totalItems = 0;
    this.pageData.totalPages = 0;
    this.data = _.clone([]);
  }

  @Mutation
  getOrderConsigneeContactsPending() {
    this.error = false;
  }

  @Mutation
  getOrderConsigneeContactsSuccess(response) {
    this.currentOrderConsigneeContacts = response.data;
  }

  @Mutation
  getOrderConsigneeContactsFailure(error) {
    this.error = true;
    this.errorMessage = error.msg;
  }

  @Mutation
  getOrderReferenceNumbersPending() {
    this.error = false;
  }

  @Mutation
  getOrderReferenceNumbersSuccess(response) {
    this.currentOrderReferenceNumbers = response.data;
  }

  @Mutation
  getOrderReferenceNumbersFailure(error) {
    this.error = true;
    this.errorMessage = error.msg;
  }

  @Mutation
  getOrderBarcodesPending() {
    this.error = false;
  }

  @Mutation
  getOrderBarcodesSuccess(response) {
    this.currentOrderBarcodeDetails = response.data;
  }

  @Mutation
  getOrderBarcodesFailure(error) {
    this.error = true;
    this.errorMessage = error.msg;
  }

  @Mutation
  getOrderBarcodeLocationsPending() {
    this.error = false;
  }

  @Mutation
  getOrderBarcodeLocationsSuccess(response) {
    this.currentOrderBarcodeLocationDetails = response.data;
  }

  @Mutation
  getOrderBarcodeLocationsFailure(error) {
    this.error = true;
    this.errorMessage = error.msg;
  }

  @Mutation
  getOrderShipsWithOrdersPending() {
    this.error = false;
  }

  @Mutation
  getOrderShipsWithOrdersSuccess(response) {
    this.currentOrderShipsWithOrders = response.data;
  }

  @Mutation
  getOrderShipsWithOrdersFailure(error) {
    this.error = true;
    this.errorMessage = error.msg;
  }

  @Mutation
  updateShipDate(newDate) {
    this.currentOrder.shipDate = newDate;
  }

  @Mutation
  updateAnticipatedReceiveDate(newDate) {
    this.currentOrder.anticipatedReceiveDate = newDate;
  }

  @Mutation
  updateRequestedDeliveryDate(newDate) {
    this.currentOrder.requestedDeliveryDate = newDate;
  }

  @Mutation
  updateFollowUpDate(newDate) {
    this.currentOrder.followUpDate = newDate;
  }

  @Mutation
  updateDockSearchDate(newDate) {
    this.currentOrder.dockSearchDate = newDate;
  }

  @Mutation
  updateFacility(facility) {
    this.currentOrder.facility = facility;
  }

  @Mutation
  updateDeliveryType(deliveryType) {
    this.currentOrder.deliveryType = deliveryType;
  }

  @Mutation
  updateCrn(crn) {
    this.currentOrder.crn = crn;
  }

  @Mutation
  updateManufacturer(manufacturer) {
    this.currentOrder.manufacturer = manufacturer;
  }

  @Mutation
  updateOrderType(orderType) {
    this.currentOrder.orderType = orderType;
  }

  @Mutation
  updateRateBy(value) {
    this.currentOrder.rateBy = value;
  }

  @Mutation
  updateOrderStatus(status) {
    this.currentOrder.status = status;
  }

  @Mutation
  updateIntermediateConsignee(intermediateConsignee) {
    this.currentOrder.intermediateConsignee = intermediateConsignee;
  }

  @Mutation
  updateRemarks(remarks) {
    this.currentOrder.remarks = remarks;
  }

  @Mutation
  updateSpecialRequests(value) {
    this.currentOrder.specialRequests = value;
  }

  @Mutation
  updateDriverNote(value) {
    this.currentOrder.driverNote = value;
  }

  @Mutation
  updateCarrier(value) {
    this.currentOrder.carrier = value;
  }

  @Mutation
  updateActualReceivedDate(value) {
    this.currentOrder.actualReceiveDate = value;
  }

  @Mutation
  updateScheduledDeliveryDate(value) {
    this.currentOrder.scheduledDeliveryDate = value;
  }

  @Mutation
  addNewReferenceNumberToCurrentOrder(number) {
    this.currentOrderReferenceNumbers.unshift(number);
  }

  @Mutation
  removeReferenceNumberFromCurrentOrder(refNum) {
    var itemsToRemove = this.currentOrderReferenceNumbers.filter(x=> x.id == refNum);
    const index = this.currentOrderReferenceNumbers.indexOf(itemsToRemove[0]);
    this.currentOrderReferenceNumbers.splice(index, 1);
  }

  @Mutation
  setReferenceNumberForCurrentOrder(refNum) {
    const index = this.currentOrderReferenceNumbers.findIndex(el => el.id === refNum.id);
    this.currentOrderReferenceNumbers = [
      ...this.currentOrderReferenceNumbers.slice(0, index),
      refNum,
      ...this.currentOrderReferenceNumbers.slice(index + 1)
    ];
  }

  @Mutation
  resetCurrentOrder() {
    this.currentOrder = _.cloneDeep(defaultOrder);
    this.currentOrderCommodities = [];
  }

  @Mutation
  updateCurrentOrderParty(payload) {
    let order = _.cloneDeep(this.currentOrder);
    switch (payload.type) {
      case "orderby":
        order.orderBy = payload.party;
        break;
      case "shipper":
        order.shipper = payload.party;
        break;
      case "consignee":
        order.consignee = payload.party;
        order.contactCompanyId = payload.party.id;
        break;
    }
    this.currentOrder = order;
  }

  @Mutation
  setCommodityToCurrentOrder(commodity) {
    this.currentOrderCommodities.unshift(Object.assign({}, commodity));
  }

  @Mutation
  updateBillTo(billTo) {
    this.currentOrder.billTo.id = billTo;
  }

  @Mutation
  setSelectOrder(payload) {
    var index = this.data.findIndex(item => {
      return item.number === payload.number;
    });
    if (index < 0) {
      return;
    }
    const existing: any = this.data[index];
    existing.selected = payload.selected;
    this.data = [...this.data.slice(0, index), existing, ...this.data.slice(index + 1)];
  }

  @Mutation
  unselectAll() {
    let newData = this.data.map(order => {
      order.selected = false;
      return order;
    });
    this.data = newData;
  }

  @Mutation
  setOrderCommodities(payload: Array<ICommodity>) {
    this.currentOrderCommodities = payload;
  }

  @Mutation
  addOrderCommodity(item: IOrderCommodity) {
    this.currentOrderCommodities.push(item);
  }

  @Mutation
  deleteOrderCommodity(index: number) {
    this.currentOrderCommodities.splice(index, 1);
  }

  @Mutation
  setOrderThumbnails(payload) {
    this.currentOrderThumbnails = payload.thumbnails;
  }

  @Mutation
  setOrderImages(payload) {
    this.currentOrderImages = payload;
  }

  @Mutation
  updateCodPending(payload) {
    this.currentOrder.codPending = payload;
  }

  @Mutation
  updateAddressVerificationStatusId(payload) {
    this.currentOrder.addressVerificationStatusId = payload;
  }

  @Mutation
  updateIsAsapDelivery(payload) {
    this.currentOrder.isAsapDelivery = payload;
  }

  @Mutation
  removeNoteFromCurrentOrder(payload) {
    if (payload.orderNumber !== this.currentOrder.number) {
      return;
    }
    const index = this.currentOrderNotes.map((x: any) => x.id).indexOf(payload.noteId);
    if (index < 0) {
      return;
    }
    this.currentOrderNotes.splice(index, 1);
  }

  @Mutation
  setOrderNotes(payload) {
    this.currentOrderNotes = payload;
  }

  @Mutation
  setOrderSort(key) {
    if (this.dataSortKey === key) this.dataSortAscending = !this.dataSortAscending;
    else {
      this.dataSortKey = key;
      this.dataSortAscending = true;
    }
  }

  @Mutation
  setUserFields(payload) {
    const fieldKeys = payload.map(x => x.key);
    this.userFields = fieldKeys;
  }

  @Mutation
  updateOrderInList(order) {
    var index = this.data.findIndex(function(item, i) {
      return item.headerNumber == order.headerNumber;
    });

    if (index < 0) {
      return;
    }

    this.data = [...this.data.slice(0, index), order, ...this.data.slice(index + 1)];
  }

  @Mutation
  setSearchResults(results) {
    this.data = results;
  }

  @Mutation
  removeContactFromList(payload) {
    const index = this.currentOrderConsigneeContacts.map((x: any) => x.id).indexOf(payload);
    if (index < 0) {
      return;
    }
    this.currentOrderConsigneeContacts.splice(index, 1);
  }

  @Mutation
  setAlertTypes(payload) {
    this.alertTypes = payload;
  }

  @Mutation
  setUncompletedOrderAlerts(payload) {
    this.uncompletedOrderAlerts = payload ? payload : 0;
  }

  @Mutation
  removeCommodityFromOrder(payload) {
    const index = this.currentOrderCommodities
      .map((x: any) => x.commodityCode)
      .indexOf(payload.commodityCode);
    if (index < 0) {
      return;
    }
    this.currentOrderCommodities.splice(index, 1);
  }

  @Mutation
  setCurrentPage(number) {
    this.pageData.currentPage = number;
  }

  @Mutation
  setAdvancedSearchFilter(filter) {
    this.data = _.clone([]);
    this.renderAdvancedSearchResults = true;
    this.advancedSearchFilter = filter;
  }

  @Mutation
  setPageSize(value) {
    this.pageData.pageSize = value;
  }

  @Action
  async reloadOrders(orderParams = { page: 1 }) {
    this.context.commit("resetOrders");
    try {
      await this.context.dispatch("loadOrders", orderParams);
    } catch (ex) {
      throw ex;
    }
  }

  @Action
  updateRowSelectionStatus(row: any) {
    this.context.commit("setSelectOrder", row);
  }

  @Action
  async getOrders(request: any) {
    const pageSize = request.pageSize || this.pageData.pageSize || 30;
    const page = request.pageNumber || 1;

    const params: string[] = [];

    params.push(addUrlParameter("page", page + ""));
    params.push(addUrlParameter("pageSize", pageSize + ""));

    if (request.orderNumber) {
      params.push(addUrlParameter("orderNumber", String(request.orderNumber)));
    }

    if (request.crn) {
      params.push(addUrlParameter("crn", request.crn));
    }

    if (request.shipsWithOrderNumber) {
      params.push(addUrlParameter("shipsWith", String(request.shipsWithOrderNumber)));
    }

    if (request.facilityCodes) {
      request.facilityCodes.forEach(facility => {
        params.push(addUrlParameter("facilityCodes", facility));
      });
    }

    if (request.billTos) {
      request.billTos.forEach(billTo => {
        params.push(addUrlParameter("billTos", billTo));
      });
    }

    if (request.zipZip3s) {
      request.zipZip3s.forEach(zip => {
        params.push(addUrlParameter("zipZip3s", zip));
      });
    }

    if (request.excludeZipZip3s) {
      request.zipZip3s.forEach(zip => {
        params.push(addUrlParameter("excludeZipZip3s", zip));
      });
    }

    if (request.manufacturers) {
      request.manufacturers.forEach(manufacturer => {
        params.push(addUrlParameter("manufacturers", manufacturer));
      });
    }

    if (request.deliveryTypes) {
      request.deliveryTypes.forEach(deliveryType => {
        params.push(addUrlParameter("deliveryTypes", deliveryType));
      });
    }

    if (request.consigneeAddress) {
      params.push(addUrlParameter("consigneeAddress", request.consigneeAddress));
    }

    if (request.consigneeName) {
      params.push(addUrlParameter("consigneeName", request.consigneeName));
    }

    if (request.consigneePhone) {
      params.push(addUrlParameter("consigneePhone", request.consigneePhone));
    }

    if (request.deliveryDate) {
      const startRange = moment(request.deliveryDate).format("YYYY-MM-DDTHH:mm:ss");
      const endRange = moment(request.deliveryDate)
        .add(1, "days")
        .subtract("1", "second")
        .format("YYYY-MM-DDTHH:mm:ss");

      params.push(addUrlParameter("ScheduledDeliveryDateStart", startRange));
      params.push(addUrlParameter("ScheduledDeliveryDateEnd", endRange));
    }

    if (request.hideManifestedOrders) {
      params.push(addUrlParameter("HideManifestedOrders", "true"));
    }

    if (request.useTruckZipsTruckId) {
      params.push(addUrlParameter("useTruckZipsTruckId", request.useTruckZipsTruckId));
    }

    if (request.excludeWillCall) {
      params.push(addUrlParameter("excludeWillCall", request.excludeWillCall));
    }

    const url = `/orders?${params.join("&")}`;

    const response = await rawApi.get<OrderListItemResource[]>(url);

    const result: GetOrdersResponse = { items: response.data, totalItems: 0, totalPages: 0 };

    if (response.headers.totalitems) result.totalItems = response.headers.totalitems;

    if (response.headers.totalpages) result.totalPages = response.headers.totalpages;

    return result;
  }

  @Action
  async reloadAdvancedSearchResults(orderParams = { page: 1 }) {
    this.context.commit("resetOrders", orderParams);
    await this.context.dispatch("loadAdvancedSearch", orderParams);
  }

  @Action
  async loadQuickSearch(orderParams: any) {
    this.context.commit("resetOrders", orderParams);
    this.quickSearchText = orderParams.text;
    this.quickSearchType = orderParams.type;

    const pageSize = orderParams.pageSize || this.pageData.pageSize || 30;
    const page = orderParams.page || this.pageData.currentPage + 1;

    let payload: any = {
      page: page,
      pageSize: pageSize
    };

    var startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 3);

    payload.StartDateStart = "2010-01-01T04:00:00.000Z";
    payload.StartDateEnd = new Date().toJSON();
    if (this.quickSearchType === "CDS") {
      payload.OrderNumber = isNaN(parseInt(this.quickSearchText))
        ? 0
        : parseInt(this.quickSearchText);
    } else if (this.quickSearchType === "REF") {
      payload.RefNumbers = [
        {
          type: null,
          number: this.quickSearchText,
          id: "ref_" + this.quickSearchText
        }
      ];
    } else if (this.quickSearchType === "ZIP") {
      payload.zipZip3s = [this.quickSearchText];
      payload.allowClosed = false;
    } else if (this.quickSearchType === "ConsigneeName") {
      payload.StartDateStart = startDate.toJSON();
      payload.consigneeName = this.quickSearchText;
      payload.allowClosed = false;
    } else if (this.quickSearchType === "ConsigneePhone") {
      payload.StartDateStart = startDate.toJSON();
      payload.ConsigneePhone = this.quickSearchText.replace(/[\s-().]/g, "");
      payload.allowClosed = false;
    } else {
      payload[this.quickSearchType] = this.quickSearchText;
    }

    if (this.dataSortKey) {
      payload["sortField"] = this.dataSortKey;
      payload["SortAscending"] = this.dataSortAscending ? true : false;
    }

    this.context.commit("setLoading", true);

    await rawApi
      .post<any>(`/orders/quickSearch`, payload)
      .then(res => {
        if (res.data.length === 1) {
          router.push({ path: "/order/" + res.data[0].number }).catch(ex => {
            if (ex.name !== "NavigationDuplicated") {
              throw ex;
            }
          });
        } else {
          this.context.commit("setOrders", res);
          this.context.commit("setLoading", false);
          this.context.commit("setCurrentPage", page);
          if (orderParams.quickSearch) {
            router.push({ name: "SearchResults" }).catch(ex => {
              console.warn("error " + ex);
            });
          }
        }
      })
      .catch(ex => {
        notifications.error("Order search error", "Failed to search order.", ex);
      })
      .finally(() => {
        this.context.commit("setLoading", false);
      });
  }

  @Action
  async loadAdvancedSearch(orderParams) {
    const pageSize = orderParams.pageSize || this.pageData.pageSize || 30;
    const page = orderParams.page || this.pageData.currentPage + 1;
    let payload: any = {
      page: page,
      pageSize: pageSize
    };

    if (!orderParams.quickSearch) {
      const filter = this.advancedSearchFilter;

      if (filter) {
        for (let fieldIndex = 0; fieldIndex < filter.searchFields.length; fieldIndex++) {
          const field = filter.searchFields[fieldIndex];
          var relatedFilter = ORDER_FILTER_FIELDS.find(x => x.name === field.name);

          if (!relatedFilter) continue;

          if (field.name === "ConsigneePhone") {
            field.filterValues[0] = field.filterValues[0].replace(/[\s-().]/g, "");
          }

          if (
            field.filterValues.length > 0 &&
            (typeof field.filterValues[0] === "string" ||
              field.filterValues[0] instanceof String) &&
            field.filterValues[0].indexOf("~") > -1 &&
            relatedFilter.type == "range"
          ) {
            const dateRangeValues = field.filterValues[0].split("~");
            payload[`${field.name}Start`] = new Date(dateRangeValues[0].trim());
            payload[`${field.name}End`] = new Date(dateRangeValues[1].trim());
          } else {
            if (relatedFilter.type == "int") {
              var numberValues = field.filterValues.map(Number);
              if (relatedFilter.multiple) payload[field.name] = numberValues;
              else payload[field.name] = numberValues[0];
            } else if (relatedFilter.type == "bool") {
              if (field.filterValues[0] === "true") payload[field.name] = true;
              else if (field.filterValues[0] === "false") payload[field.name] = false;
            } else if (relatedFilter.type == "range") {

              if(field.filterValues[0] === "noDate"){
                payload[`${field.name}Start`] = "2049-12-31";
                payload[`${field.name}End`] = "2049-12-31";
              } else {
                if (isDynamicDateRange(field.filterValues[0])) {
                  payload[`${field.name}Start`] = new Date(
                    startDateFromDynamicRange(field.filterValues[0]).format("MM/DD/YYYY")
                  );
                  payload[`${field.name}End`] = new Date(
                    endDateFromDynamicRange(field.filterValues[0]).format("MM/DD/YYYY")
                  );
                }
              }
            } else {
              if (relatedFilter.multiple) payload[field.name] = field.filterValues;
              else payload[field.name] = field.filterValues[0];
            }
          }
        }
      }
    } else {
      payload.StartDateStart = "2010-01-01T04:00:00.000Z";
      payload.StartDateEnd = new Date().toJSON();
      if (this.quickSearchType === "CDS") {
        payload.OrderNumber = isNaN(parseInt(this.quickSearchText))
          ? 0
          : parseInt(this.quickSearchText);
      } else if (this.quickSearchType === "REF") {
        payload.RefNumbers = [
          {
            type: null,
            number: this.quickSearchText,
            id: "ref_" + this.quickSearchText
          }
        ];
      } else if (this.quickSearchType === "ZIP") {
        payload.zipZip3s = [this.quickSearchText];
        payload.allowClosed = false;
      } else if (this.quickSearchType === "ConsigneeName") {
        payload[this.quickSearchType] = this.quickSearchText;
        payload.allowClosed = false;
      } else if (this.quickSearchType === "ConsigneePhone") {
        payload[this.quickSearchType] = this.quickSearchText;
        payload.allowClosed = false;
      } else {
        payload[this.quickSearchType] = this.quickSearchText;
      }
    }

    if (this.dataSortKey) {
      payload["sortField"] = this.dataSortKey;
      payload["SortAscending"] = this.dataSortAscending ? true : false;
    }

    this.context.commit("setLoading", true);

    await rawApi
      .post<any>(`/orders/advancedSearch`, payload)
      .then(res => {
        if (res.data.length === 1) {
          router.push({ path: "/order/" + res.data[0].number }).catch(ex => {
            if (ex.name !== "NavigationDuplicated") {
              throw ex;
            }
          });
        } else {
          this.context.commit("setOrders", res);
          this.context.commit("setLoading", false);
          this.context.commit("setCurrentPage", page);
          if (orderParams.quickSearch) {
            router.push({ name: "SearchResults" }).catch(ex => {
              console.warn("error " + ex);
            });
          }
        }
      })
      .catch(ex => {
        notifications.error("Order search error", "Failed to search order.", ex);
      })
      .finally(() => {
        this.context.commit("setLoading", false);
      });
  }

  @Action
  async loadOrders(orderParams: any) {
    const pageSize =
      orderParams && orderParams.pageSize ? orderParams.pageSize : null || this.pageData.pageSize;
    const page =
      orderParams && orderParams.page ? orderParams.page : null || this.pageData.currentPage + 1;

    let filter = this.context.rootGetters["account/getAdhocFilterIfActive"];

    if (!filter) filter = this.context.rootGetters["account/getSelectedSavedOrderSearch"];

    let payload: any = {
      page: page,
      pageSize: pageSize
    };
    if (filter) {
      for (let fieldIndex = 0; fieldIndex < filter.searchFields.length; fieldIndex++) {
        const field = filter.searchFields[fieldIndex];
        var relatedFilter = ORDER_FILTER_FIELDS.find(x => x.name === field.name);

        if (!relatedFilter) continue;

        if (field.name === "ConsigneePhone") {
          field.filterValues[0] = field.filterValues[0].replace(/[\s-().]/g, "");
        }

        if (
          field.filterValues.length > 0 &&
          (typeof field.filterValues[0] === "string" ||
            field.filterValues[0] instanceof String) &&
          field.filterValues[0].indexOf("~") > -1 &&
          relatedFilter.type == "range"
        ) {
          const dateRangeValues = field.filterValues[0].split("~");
          payload[`${field.name}Start`] = new Date(dateRangeValues[0].trim());
          payload[`${field.name}End`] = new Date(dateRangeValues[1].trim());
        } else {
          if (relatedFilter.type == "int") {
            var numberValues = field.filterValues.map(Number);
            if (relatedFilter.multiple) payload[field.name] = numberValues;
            else payload[field.name] = numberValues[0];
          } else if (relatedFilter.type == "bool") {
            if (field.filterValues[0] === "true") payload[field.name] = true;
            else if (field.filterValues[0] === "false") payload[field.name] = false;
          } else if (relatedFilter.type == "range") {

            if(field.filterValues[0] === "noDate"){
              payload[`${field.name}Start`] = "2049-12-31";
              payload[`${field.name}End`] = "2049-12-31";
            } else {
              if (isDynamicDateRange(field.filterValues[0])) {
                payload[`${field.name}Start`] = new Date(
                  startDateFromDynamicRange(field.filterValues[0]).format("MM/DD/YYYY")
                );
                payload[`${field.name}End`] = new Date(
                  endDateFromDynamicRange(field.filterValues[0]).format("MM/DD/YYYY")
                );
              }
            }
          } else {
            if (relatedFilter.multiple) payload[field.name] = field.filterValues;
            else payload[field.name] = field.filterValues[0];
          }
        }
      }
    }

    if (this.dataSortKey) {
      payload["sortField"] = this.dataSortKey;
      payload["SortAscending"] = this.dataSortAscending ? true : false;
    }

    this.context.commit("setLoading", true);
    try {
      const response = await rawApi.post<any>(
        `/orders/search`, payload
      );
      this.context.commit("setOrders", response);
      this.context.commit("setLoading", false);
      this.context.commit("setCurrentPage", page);
    } catch (ex) {
      this.context.commit("setLoading", false);
      throw ex;
    }
  }

  @Action
  async exportOrders() {
    let filter = this.context.rootGetters["account/getAdhocFilterIfActive"];

    if (!filter) filter = this.context.rootGetters["account/getSelectedSavedOrderSearch"];

    let filterData = "";
    if (filter) {
      const parameters = [];

      for (let fieldIndex = 0; fieldIndex < filter.searchFields.length; fieldIndex++) {
        const field = filter.searchFields[fieldIndex];

        if (field.dateType && field.dateType === "dynamic") {
          if(field.filterValues[0] !== "noDate") {
            field.filterValues[0] = formatDynamicDate(field.filterValues[0]);
          }
        }

        if (field.name == "RefNumbers") {
          field.filterValues.forEach((fv, index) => {
            let str = `RefNumbers[${index}].Type=${encodeURIComponent(
              fv.type
            )}&RefNumbers[${index}].Number=${encodeURIComponent(fv.number)}`;
            parameters.push(str);
          });
        } else if(field.dateType && field.dateType === "dynamic" && field.filterValues[0] === "noDate") {
          const encodeDate = encodeURIComponent("12/31/2049");
          parameters.push(`${field.name}Start=${encodeDate}`);
          parameters.push(`${field.name}End=${encodeDate}`);
        } else {
          field.filterValues.forEach(item => {
            if (item && typeof item === "string" && item.indexOf(" ~ ") > -1) {
              const dateRangeValues = item.split("~");
              parameters.push(
                `${field.name}Start=${encodeURIComponent(dateRangeValues[0].trim())}&${
                  field.name
                }End=${encodeURIComponent(dateRangeValues[1].trim())}`
              );
            } else {
              parameters.push(`${field.name}=${encodeURIComponent(item)}`);
            }
          });
        }
      }

      filterData = parameters.join("&");
    }

    if (this.dataSortKey) {
      filterData += `&sortField=${this.dataSortKey}&SortAscending=${
        this.dataSortAscending ? "true" : "false"
      }`;
    }

    this.context.commit("setLoading", true);
    try {
      const response = await rawApi.post<any>(`/orders/export`, filterData, {
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `orders.csv`);
      document.body.appendChild(link);
      link.click();

      this.context.commit("setLoading", false);
    } catch (ex) {
      this.context.commit("setLoading", false);
      throw ex;
    }
  }

  @Action
  async exportDeliveryExport(request: DeliveryExportRequest) {
    this.context.commit("setLoading", true);
    try {
      const response = await rawApi.get<any>(
        `/orders/deliveryExport?format=${request.format}&facilityCode=${request.facilityCode}&deliveryDateStart=${request.deliveryDateStart}&deliveryDateEnd=${request.deliveryDateEnd}`,
        {
          responseType: "blob"
        }
      );
      const contentType = response.headers["Content-Type"];

      const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", getFileName(response));
      document.body.appendChild(link);
      link.click();

      this.context.commit("setLoading", false);
    } catch (ex) {
      this.context.commit("setLoading", false);
      throw ex;
    }
  }

  @Action
  async exportCompletedOrdersExport(request: CompletedOrderExportRequest) {
    this.context.commit("setLoading", true);
    try {
      const response = await rawApi.get<any>(
        `/orders/completedOrderExport?format=${request.format}&deliveryDateStart=${request.deliveryDateStart}&deliveryDateEnd=${request.deliveryDateEnd}&scheduledDates=${request.include.scheduledDates}&receiptDate=${request.include.receiptDate}&loadingDate=${request.include.loadingDate}&deliveryDate=${request.include.deliveryDate}&documents=${request.include.documents}&pods=${request.include.pods}`,
        {
          responseType: "blob"
        }
      );
      const contentType = response.headers["Content-Type"];

      const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", getFileName(response));
      document.body.appendChild(link);
      link.click();

      this.context.commit("setLoading", false);
    } catch (ex) {
      this.context.commit("setLoading", false);
      throw ex;
    }
  }

  @Action
  async exportAdvancedSearchOrders() {
    let payload = {};
    const filter = this.advancedSearchFilter;
    if (filter) {
      for (let fieldIndex = 0; fieldIndex < filter.searchFields.length; fieldIndex++) {
        const field = filter.searchFields[fieldIndex];
        var relatedFilter = ORDER_FILTER_FIELDS.find(x => x.name === field.name);

        if (!relatedFilter) continue;
        if (field.dateType && field.dateType === "dynamic") {
          if(field.filterValues[0] === "noDate") {
            payload[`${field.name}Start`] = "2049-12-31";
            payload[`${field.name}End`] = "2049-12-31";
          } else {
            field.filterValues[0] = formatDynamicDate(field.filterValues[0]);
          }
        }

        if (field.name === "ConsigneePhone") {
          field.filterValues[0] = field.filterValues[0].replace(/[\s-().]/g, "");
        }
        if (
          field.filterValues.length > 0 &&
          (typeof field.filterValues[0] === "string" || field.filterValues[0] instanceof String) &&
          field.filterValues[0].indexOf("~") > -1
        ) {
          const dateRangeValues = field.filterValues[0].split(" ~ ");
          payload[`${field.name}Start`] = new Date(dateRangeValues[0].trim());
          payload[`${field.name}End`] = new Date(dateRangeValues[1].trim());
        } else {
          if (relatedFilter.type == "int") {
            var numberValues = field.filterValues.map(Number);
            if (relatedFilter.multiple) payload[field.name] = numberValues;
            else payload[field.name] = numberValues[0];
          } else if (relatedFilter.type == "bool") {
            if (field.filterValues[0] === "true") payload[field.name] = true;
            else if (field.filterValues[0] === "false") payload[field.name] = false;
          } else {
            if (relatedFilter.multiple) payload[field.name] = field.filterValues;
            else payload[field.name] = field.filterValues[0];
          }
        }
      }
    }

    this.context.commit("setLoading", true);
    try {
      const response = await rawApi.post<any>(`/orders/advancedSearchExport`, payload);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `advancedSearch.csv`);
      document.body.appendChild(link);
      link.click();

      this.context.commit("setLoading", false);
    } catch (ex) {
      this.context.commit("setLoading", false);
      throw ex;
    }
  }

  @Action
  async exportQuickSearchOrders() {
    let payload = {};

    payload["StartDateStart"] = "2010-01-01T04:00:00.000Z";
    payload["StartDateEnd"] = new Date().toJSON();

    var startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 3);

    if (this.quickSearchType === "CDS") {
      payload["OrderNumber"] = isNaN(parseInt(this.quickSearchText))
        ? 0
        : parseInt(this.quickSearchText);
    } else if (this.quickSearchType === "REF") {
      payload["RefNumbers"] = [
        {
          type: null,
          number: this.quickSearchText,
          id: "ref_" + this.quickSearchText
        }
      ];
    } else if (this.quickSearchType === "ZIP") {
      payload["zipZip3s"] = [this.quickSearchText];
      payload["allowClosed"] = false;
    } else if (this.quickSearchType === "ConsigneeName") {
      payload["startDateStart"] = startDate.toJSON();
      payload["consigneeName"] = this.quickSearchText;
      payload["allowClosed"] = false;
    } else if (this.quickSearchType === "ConsigneePhone") {
      payload["startDateStart"] = startDate.toJSON();
      payload["consigneePhone"] = this.quickSearchText.replace(/[\s-().]/g, "");
      payload["allowClosed"] = false;
    } else {
      payload[this.quickSearchType] = this.quickSearchText;
    }

    this.context.commit("setLoading", true);
    try {
      const response = await rawApi.post<any>(`/orders/quickSearchExport`, payload);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `quickSearch.csv`);
      document.body.appendChild(link);
      link.click();

      this.context.commit("setLoading", false);
    } catch (ex) {
      this.context.commit("setLoading", false);
      throw ex;
    }
  }

  @Action
  async fetchOrder(orderNumber) {
    return await api.getAsync(`/orders/${orderNumber}`);
  }

  @Action
  toggleOrderField(orderFieldChange) {
    this.context.commit("setOrderField", orderFieldChange);
  }

  @Action
  updateOrderFields(orderFields) {
    this.context.commit("setOrderFields", orderFields);
  }

  @Action
  async getOrderPreview(orderId) {
    await api.getAsync(`/orders/${orderId}`).then(res => {
      this.context.commit("setCurrentOrderPreview", res.data);
    });

    await this.getOrderCommodities(orderId).then(res => {
      this.context.commit("setOrderPreviewCommodities", res);
    });

    await api.getAsync(`/orders/${orderId}/previewOrderDetail`).then(result => {
      this.context.commit("setOrderPreviewOrderItems", result);
    })
  }

  @Action
  async getOrder(orderId) {
    await api
      .getAsync(`/orders/${orderId}`)
      .then(res => {
        this.context.commit("setCurrentOrder", res.data);
      })
      .catch(ex => {
        notifications.error("Order loading error", "Failed to load order.", ex);
        this.context.commit("setLoading", false);
        throw ex;
      });
  }

  @Action
  async deleteOrder(orderId) {
    await api.deleteAsync(`orders/${orderId}`);
  }

  @Action
  async updateOrder(order) {
    await api.put(`order/${order.number}`, { order }, "getOrderNote", this.context);
  }

  @Action
  async updateEstimatedReceiveDate(payload: {
    orderNumbers: number[];
    anticipatedReceiveDate: Date | string;
    orderNote?: {
      text: string;
      visibleOption?: string;
      facilityViewable: boolean;
      customerViewable: boolean;
    } | null;
  }) {
    const response = await rawApi.post(`/orders/estimatedReceiveDate`, {
      orderNumbers: payload.orderNumbers,
      anticipatedReceiveDate: payload.anticipatedReceiveDate,
      orderNote: payload.orderNote ?? null
    });
    return response.data;
  }

  @Action
  async receiveOrder(order) {
    await api.postAsync(`orders/${order.orderHeaderNumber}/receive`, order);
  }

  @Action
  async scheduleOrder(order) {
    await api.postAsync(`/orders/${order.orderNumber}/schedule`, order);
  }

  @Action
  async unscheduleOrder(payload) {
    await api.postAsync(`/orders/unschedule`, payload);
  }

  @Action
  async orderTimeframe(order) {
    await api.postAsync(`/orders/${order.number}/timeframe`, order);
  }

  @Action
  async consolidateOrder(order) {
    await api.post(`/orders/${order.number}/consolidated`, {}, "consolidateOrder", this.context);
  }

  @Action
  async getOrderNotesForOrder(order) {
    return await api.getAsync(`/orders/${order.number}/notes`).catch(ex => {
      notifications.error("Notes loading error", "Failed to load notes.", ex);
    });
  }

  @Action
  async getOrderNotes(order) {
    if (order && order.number) {
      await api.get(`/orders/${order.number}/notes`, "getOrderNotes", this.context);
    }
  }

  @Action
  async getOrderNote(orderNote) {
    api.get(`/orders/${orderNote.orderId}/notes/${orderNote.id}`, "getOrderNote", this.context);
  }

  @Action
  async getAddressChangeRequest(orderNumber) {
    return await api.getAsync(`/orders/${orderNumber}/AddressChangeRequest`).catch(ex => {
      notifications.error("Notes loading error", "Failed to load notes.", ex);
    });
  }

  @Action
  async createOrderNotes(orderNote) {
    var isAddToShipsWithOrders =
      orderNote.addWithShipWithOrders !== undefined ? orderNote.addWithShipWithOrders : false;

    await api
      .postAsync(
        `/orders/${orderNote.orderHeaderNumber}/notes?isAddToShipsWithOrders=${isAddToShipsWithOrders}`,
        orderNote
      )
      .then(() => {
        this.getOrderNotes(this.currentOrder);
      });
  }

  @Action
  async updateOrderNote(orderNote) {
    await api.put(
      `/orders/${orderNote.orderId}/notes/${orderNote.id}`,
      { orderNote },
      "updateOrderNote",
      this.context
    );
  }

  @Action
  async deleteOrderNote(orderNote) {
    return await rawApi
      .delete(`/orders/${orderNote.orderId}/notes/${orderNote.id}`, {
        data: { isLegacy: orderNote.isLegacy }
      })
      .then(response => {
        this.context.commit("removeNoteFromCurrentOrder", {
          orderNumber: orderNote.orderId,
          noteId: orderNote.id
        });
        notifications.success("Note deleted", "Note successfully deleted.");
        return response;
      })
      .catch(ex => {
        notifications.error("Note deletion error", "Failed to delete note.", ex);
        return ex;
      });
  }

  @Action
  async getOrderConsigneeContacts(orderId) {

    await api
      .get(`/orders/${orderId}/consigneeContacts`, "getOrderConsigneeContacts", this.context)
      .then(res => {
        this.context.commit("getOrderConsigneeContactsSuccess", res);
      });
      await this.getCurrentDeliveryProfile(this.currentOrder.number);
  }

  @Action
  async getOrderReferenceNumbers(order) {
    await api.get(
      `/orders/${order.number}/referenceNumbers`,
      "getOrderReferenceNumbers",
      this.context
    );
  }

  @Action
  async getOrderBarcodes(order) {
    await api.get(`/orders/${order.number}/barcode`, "getOrderBarcodes", this.context);
  }

  @Action
  async getOrderBarcodeLocations(order) {
    await api.get(
      `/orders/${order.number}/barcode/locations`,
      "getOrderBarcodeLocations",
      this.context
    );
  }

  @Action
  async getOrderShipsWithOrders(order) {
    api.get(`/orders/${order.number}/shipsWithOrders`, "getOrderShipsWithOrders", this.context);
  }

  @Action
  async loadOrder(orderNumber) {
    this.context.commit("setLoading", true);
    await this.context
      .dispatch("getOrder", orderNumber)
      .then(() => {
        this.context.dispatch("getOrderConsigneeContacts", orderNumber);
      })
      .then(() => this.context.dispatch("loadOrderCommodities", orderNumber));
  }

  @Action
  reloadOrderShortcutAction() {
    this.context.dispatch("loadOrder", this.currentOrder.number);
  }

  @Action
  async loadUncompletedOrderAlerts() {
    const accountModule = getModule(AccountModule);
    let url = `/orderAlerts/uncompletedOrders`;

    return api
      .getAsync(url)
      .then(res => {
        this.context.commit("setUncompletedOrderAlerts", res.data);
      })
      .catch(ex => {
        notifications.error(
          "Failed to load uncompleted order alerts",
          "Failed to load uncompleted order alerts",
          ex
        );
      });
  }

  @Action
  async printBolShortcutAction() {
    const response = await rawApi.get(`/orders/${this.currentOrder.number}/printBol`, {
      responseType: "blob"
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${this.currentOrder.number}-bol.pdf`);
    document.body.appendChild(link);
    link.click();
  }

  @Action
  notesShortcutAction(payload) {
    this.context.dispatch(
      `system/showModal`,
      { name: modals.notesModal, data: payload },
      { root: true }
    );
  }

  @Action
  advancedSearchShortcutAction(payload) {
    this.context.dispatch(
      `system/showModal`,
      { name: modals.advancedSearchModal, data: payload },
      { root: true }
    );
  }

  @Action
  customerNotReadyShortcutAction(payload) {
    this.context.dispatch(
      `system/showModal`,
      { name: modals.customerNotReadyModal, data: payload },
      { root: true }
    );
  }

  @Action
  updateOrderInstructionsShortcutAction(payload) {
    this.context.dispatch(
      `system/showModal`,
      { name: modals.updateOrderInstructionsModal, data: payload },
      { root: true }
    );
  }

  @Action
  orderAuditTrailShortcutAction(payload) {
    this.context.dispatch(
      `system/showModal`,
      { name: modals.orderAuditTrailModal, data: payload },
      { root: true }
    );
  }

  @Action
  newOrderShortcutAction() {
    router.push({ name: "NewOrder" });
  }

  @Action
  orderMapShortcutAction(payload) {
    this.context.dispatch(
      `system/showModal`,
      { name: modals.mapModal, data: payload },
      { root: true }
    );
  }

  @Action
  async viewBarcodeOrderShortcutAction(payload) {
    if (!payload || !payload.selectedItems || payload.selectedItems.length != 1) {
      return;
    }
    return this.context.dispatch(
      "system/showModal",
      { name: modals.barcodeOrderViewModal, data: { selectedItems: [{id: payload.selectedItems[0].barcodeOrderId }] }},
      { root: true }
    );
  }

  @Action
  refreshDispatchShortcutAction() {
    this.context.dispatch("reloadOrders").catch(ex => {
      notifications.error("Order List", "Failed to reload dispatch board", ex);
    });
  }

  @Action
  async addReferenceNumberToCurrentOrder(details) {
    await api
      .postAsync(`/orders/${this.currentOrder.number}/referenceNumbers`, details)
      .then(res => {
        details.id = res.data.id;
        this.context.commit("addNewReferenceNumberToCurrentOrder", details);
      });
  }

  @Action
  async deleteReferenceNumberFromCurrentOrder(refNum) {
    await api
      .deleteAsync(`/orders/${this.currentOrder.number}/referenceNumbers/${refNum}`)
      .then(() => {
        this.context.commit("removeReferenceNumberFromCurrentOrder", refNum);
      });
  }

  @Action
  async updateReferenceNumberForCurrentOrder(refNum) {
    await api
      .putAsync(`/orders/${this.currentOrder.number}/referenceNumbers/${refNum.id}`, refNum)
      .then(() => {
        return this.context.commit("setReferenceNumberForCurrentOrder", refNum);
      });
  }

  @Action
  async changeCurrentOrderParties(payload) {
    const validTypes = ["consignee", "shipper", "orderby"];
    payload.forEach(item => {
      if (validTypes.indexOf(item.type) < 0) {
        return;
      }
    })

    this.context.dispatch("system/updateIsSaving", true, { root: true });

    for (let i = 0; i < payload.length; i++) {
      var item = payload[i];
      await api.postAsync(`/orders/${this.currentOrder.number}/${item.type}/${item.party.id}`, {})
        .then(response => {
          this.context.commit("updateCurrentOrderParty", {
            type: item.type,
            party: response.data.party
          });
        })
        .then(() => {
          if (payload.type === "consignee") {
            this.context.commit("getOrderConsigneeContactsSuccess", { data: [] });
            this.context.dispatch("getOrderConsigneeContacts", this.currentOrder.number);
          }
        })
    }

    this.context.dispatch("system/updateIsSaving", false, { root: true });
  }

  @Action
  changeCurrentOrderParty(payload) {
    new Promise((resolve, reject) => {
      const validTypes = ["consignee", "shipper", "orderby"];

      if (validTypes.indexOf(payload.type) < 0) {
        reject("Invalid party type");
        return;
      }

      if (this.currentOrder.number) {
        this.context.dispatch("system/updateIsSaving", true, { root: true });

        api
          .postAsync(`/orders/${this.currentOrder.number}/${payload.type}/${payload.party.id}`, {})
          .then(response => {
            this.context.commit("updateCurrentOrderParty", {
              type: payload.type,
              party: response.data.party
            });
            resolve(response.data.party);
          })
          .then(() => {
            if (payload.type === "consignee") {
              this.context.commit("getOrderConsigneeContactsSuccess", { data: [] });
              this.context.dispatch("getOrderConsigneeContacts", this.currentOrder.number);
            }
          })
          .catch(ex => reject(ex))
          .finally(() => {
            this.context.dispatch("system/updateIsSaving", false, { root: true });
          });
      } else {
        this.context.commit("updateCurrentOrderParty", payload);
        resolve({});
      }
    });
  }

  @Action
  addCommodityToCurrentOrder(payload) {
    this.context.commit("setCommodityToCurrentOrder", payload);
  }

  @Action
  async addCommodity(
    payload: AddOrderCommodityRequest | EditOrderCommodityRequest | EditOrderCommodityUnitsRequest
  ) {
    await rawApi
      .post<AddOrderCommodityResponse>(`/orders/${this.currentOrder.number}/commodities`, payload)
      .then(() => this.context.dispatch("loadOrderCommodities", this.currentOrder.number));
  }

  @Action
  async editCommodity(payload: EditOrderCommodityRequest) {
    await rawApi
      .put<AddOrderCommodityResponse>(
        `/orders/${this.currentOrder.number}/commodities/${payload.originalCommodityCode}`,
        payload
      )
      .then(() => this.context.dispatch("loadOrderCommodities", this.currentOrder.number));
  }

  @Action
  async editCommodityUnits(payload: EditOrderCommodityUnitsRequest) {
    await rawApi
      .put<AddOrderCommodityResponse>(
        `/orders/${this.currentOrder.number}/commodities/${payload.commodityCode}/updateunits`,
        payload
      )
      .then(() => this.context.dispatch("loadOrderCommodities", this.currentOrder.number));
  }

  @Action
  async removeCommodity(request: RemoveOrderCommodityRequest) {
    try {
      const url = `/orders/${request.headerNumber}/commodities/${btoa(request.commodityCode)}`;
      this.context.dispatch("system/updateIsSaving", true, { root: true });
      await rawApi.delete(url);
      this.context.commit("removeCommodityFromOrder", request);
    } catch (ex) {
      throw ex;
    } finally {
      this.context.dispatch("system/updateIsSaving", false, { root: true });
    }
  }

  @Action
  async createOrder() {
    const o = this.currentOrder;

    interface OrderCommodity {
      quantity: number;
      numberOfCubes: number;
      weight: number;
      commodityCode: string | null;
    }

    const payload = {
      deliveryForId: o.deliveryForId,
      deliveryType: o.deliveryType,
      crn: o.crn,
      manufacturer: o.manufacturer,
      facility: o.facility,
      orderType: o.orderType,
      orderByCompanyId: null,
      billToCompanyId: null,
      consigneeCompanyId: null,
      shipperCompanyId: null,
      shipDate: o.shipDate,
      actualReceiveDate: o.actualReceiveDate,
      scheduledDeliveryDate: o.scheduledDeliveryDate,
      remarks: o.remarks,
      driverNote: o.driverNote,
      orderCommodities: this.currentOrderCommodities.map((x: OrderCommodity) => ({
        count: x.quantity,
        cubes: x.numberOfCubes,
        weight: x.weight,
        commodityCode: x.commodityCode
      }))
    };

    if (o.orderBy) payload.orderByCompanyId = o.orderBy.id;
    if (o.billTo) payload.billToCompanyId = o.billTo.id;
    if (o.consignee) payload.consigneeCompanyId = o.consignee.id;
    if (o.shipper) payload.shipperCompanyId = o.shipper.id;

    return api.postAsync("/orders", payload);
  }

  @Action
  async editOrderNote(note) {
    await api.putAsync(`/orders/${note.orderHeaderNumber}/notes/${note.id}`, note).then(() => {
      this.getOrderNotes(this.currentOrder);
    });
  }

  @Action showAddNoteModal({ orderNumber, displayShipWithOrderCheckbox = false }) {
    this.context.dispatch(
      "system/showModal",
      {
        name: modals.addNoteModal,
        data: { displayShipWithOrderCheckbox, selectedItems: [{ number: orderNumber }] }
      },
      { root: true }
    );
  }

  @Action editAddNoteModal(note) {
    this.context.dispatch(
      "system/showModal",
      { name: modals.addNoteModal, data: { note: note } },
      { root: true }
    );
  }

  @Action showAddConsigneeContactEntryModal() {
    this.context.dispatch(
      "system/showModal",
      { name: modals.consigneeContactEntryModal },
      { root: true }
    );
  }

  @Action showBarcodeMapModal() {
    this.context.dispatch("system/showModal", { name: modals.barcodeMapModal }, { root: true });
  }

  @Action showImagesModal() {
    this.context.dispatch("system/showModal", { name: modals.imagesModal }, { root: true });
  }

  @Action showImageUploadModal() {
    this.context.dispatch("system/showModal", { name: modals.imageUploadModal }, { root: true });
  }

  @Action showEditConsigneeContactEntryModal(contact) {
    this.context.dispatch(
      "system/showModal",
      { name: modals.consigneeContactEntryModal, data: contact },
      { root: true }
    );
  }

  @Action showConsigneeContactSearchModal() {
    this.context.dispatch(
      "system/showModal",
      { name: modals.consigneeContactSearchModal },
      { root: true }
    );
  }

  @Action showEditNoteModal(note) {
    this.context.dispatch(
      "system/showModal",
      { name: modals.addNoteModal, data: { note: note } },
      { root: true }
    );
  }

  @Action
  shipsWithOrdersShortcutAction() {
    router.push({ path: "/order/" + this.currentOrder.number + "/ships-with" });
  }

  @Action
  openOrderShortcutAction(payload) {
    if (payload && payload.selectedItems) {
      router.push({ path: "/order/" + payload.selectedItems[0].number });
    } else {
      console.warn("no selected items");
    }
  }

  @Action
  openOrderInNewTabShortcutAction(payload) {
    if (payload && payload.selectedItems) {
      window.open("/order/" + payload.selectedItems[0].number, "_blank");
    } else {
      console.warn("no selected items");
    }
  }

  @Action
  estimatedReceiveDateShortcutAction(payload) {
    this.context.dispatch(
      "system/showModal",
      { name: modals.estimatedReceiveDateModal, data: payload },
      { root: true }
    );
  }

  @Action
  orderPreviewShortcutAction(payload) {
    this.context.dispatch(
      "system/showModal",
      { name: modals.orderPreviewModal, data: payload.selectedItems[0].number },
      { root: true }
    );
  }

  @Action
  receiveOrderShortcutAction(payload) {
    this.context.dispatch(
      "system/showModal",
      { name: modals.receiveOrderModal, data: payload },
      { root: true }
    );
  }

  @Action
  messageCustomerShortcutAction(payload) {
    this.context.dispatch(
      "system/showModal",
      { name: modals.messageCustomerModal, data: payload },
      { root: true }
    );
  }

  @Action
  scheduleOrderShortcutAction(payload) {
    let isOnHold = false;

    // Prefer the selected context-menu order when present so stale detail-page state does not
    // incorrectly block scheduling on a different order.
    if (payload && payload.selectedItems && payload.selectedItems.length > 0) {
      isOnHold = payload.selectedItems.some(item => item.isOnHold || item.IsOnHold);
    } else if (this.currentOrder && this.currentOrder.isOnHold) {
      isOnHold = true;
    }

    if (isOnHold) {
      this.context.dispatch(
        "system/showModal",
        { name: modals.customerOnHoldModal, data: payload },
        { root: true }
      );
    } else {
      this.context.dispatch(
        "system/showModal",
        { name: modals.scheduleDeliveryModal, data: payload },
        { root: true }
      );
    }
  }

  @Action
  deliveryDateHistoryLogsShortcutAction(payload) {
    this.context.dispatch(
      "system/showModal",
      { name: modals.deliveryDateHistoryLogsModal, data: payload },
      { root: true }
    );
  }

  @Action
  unscheduleOrderShortcutAction(payload) {
    this.context.dispatch(
      "system/showModal",
      { name: modals.unscheduleDeliveryModal, data: payload },
      { root: true }
    );
  }

  @Action
  addOrderPalletShortcutAction(payload) {
    this.context.dispatch(
      "system/showModal",
      { name: modals.addOrderPalletsModal, data: payload },
      { root: true }
    );
  }

  @Action
  addOrderToTruckShortcutAction(payload) {
    this.context.dispatch(
      "system/showModal",
      { name: modals.addOrderToTruckModal, data: payload },
      { root: true }
    );
  }

  @Action
  removeOrderFromTruckShortcutAction(payload) {
    this.context.dispatch(
      "system/showModal",
      { name: modals.removeOrderFromTruckModal, data: payload },
      { root: true }
    );
  }

  @Action
  copyOrderShortcutAction(payload) {
    this.context.dispatch(
      "system/showModal",
      { name: modals.copyOrderModal, data: payload },
      { root: true }
    );
  }

  @Action
  setTimeframesShortcutAction(payload) {
    this.context.dispatch(
      "system/showModal",
      { name: modals.timeframeEntryModal, data: payload },
      { root: true }
    );
  }

  @Action
  setBulkOrdersWantsAsapAction(payload) {
    this.context.dispatch(
      "system/showModal",
      { name: modals.setBulkOrdersWantsAsapModal, data: payload },
      { root: true }
    );
  }

  @Action
  addNoteShortcutAction(payload) {
    this.context.dispatch(
      "system/showModal",
      { name: modals.addNoteModal, data: payload },
      { root: true }
    );
  }

  @Action
  cancelOrderShortcutAction(payload) {
    this.context.dispatch(
      "system/showModal",
      {
        name: modals.cancelOrderModal,
        data: {
          ...payload,
          noteIsRequired: true
        }
      },
      { root: true }
    );
  }

  @Action
  uncancelOrderShortcutAction(payload) {
    this.context.dispatch(
      "system/showModal",
      {
        name: modals.uncancelOrderModal,
        data: {
          ...payload,
          noteIsRequired: true
        }
      },
      { root: true }
    );
  }

  @Action
  completeOrderShortcutAction(payload) {
    this.context.dispatch(
      "system/showModal",
      { name: modals.orderCompletionModal, data: payload },
      { root: true }
    );
  }

  @Action
  loadOrderCommodities(orderNumber) {
    return api
      .getAsync(`/orders/${orderNumber}/commodities`)
      .then(res => this.context.commit("setOrderCommodities", res.data))
      .catch(ex => {
        notifications.error(
          "Failed to load order commodities",
          "Failed to load order commodities",
          ex
        );
      });
  }

  @Action
  getOrderCommodities(orderNumber) {
    return rawApi.get<CommodityResource[]>(`/orders/${orderNumber}/commodities`);
  }

  @Action
  async hasBackorders(orderNumber: number): Promise<any[]> {
    const res = await rawApi.get<any[]>(`/orders/${orderNumber}/hasBackorders`);
    return res.data;
  }

  @Action
  async hasShortages(orderNumber: number): Promise<any[]> {
    const res = await rawApi.get<any[]>(`/orders/${orderNumber}/hasShortages`);
    return res.data;
  }

  @Action
  async hasDamages(orderNumber: number): Promise<any[]> {
    const res = await rawApi.get<any[]>(`/orders/${orderNumber}/hasDamages`);
    return res.data;
  }

  @Action
  async getOrderAccessorials(orderNumber) {
    const res = await rawApi.get<OrderAccessorialResource[]>(`/orders/${orderNumber}/accessorials`);
    return res.data;
  }

  @Action
  createTruckForOrder(orderNumber) {
    return api.postAsync(`/orders/${orderNumber}/truck`, {});
  }

  @Action
  cancelOrder({ orderNumber, orderNote }) {
    return api.postAsync(`/orders/${orderNumber}/cancel`, {
      orderNumber,
      orderNote
    });
  }
  @Action
  uncancelOrder({ orderNumber, orderNote }) {
    return api.postAsync(`/orders/${orderNumber}/uncancel`, {
      orderNumber,
      orderNote
    });
  }

  @Action
  searchOrders(payload) {
    let page = 1;
    let pageSize = 30;

    if (payload.page) page = payload.page;

    if (payload.pageSize) pageSize = payload.pageSize;

    const search = payload.search;
    const searchOption = payload.searchOption;
    return api
      .getAsync(
        `/orders/search?search=${search}&page=${page}&pageSize=${pageSize}&searchOption=${searchOption}`
      )
      .then(res => {
        if (res.data.length === 1) {
          router.push({ path: "/order/" + res.data[0].number }).catch(ex => {
            if (ex.name !== "NavigationDuplicated") {
              throw ex;
            }
          });
        } else {
          this.context.commit("setOrderHeaders", res.headers);
          this.context.commit("setSearchResults", res.data);
          router.push({ name: "SearchResults" }).catch(ex => {
            console.warn("error " + ex);
          });
        }
      });
  }

  @Action
  addConsigneeContact(payload) {
    return api
      .postAsync(`/orders/${payload.orderNumber}/consigneeContacts/${payload.contactId}`, {CopyContact: payload.copyContact})
      .then(() => this.context.dispatch("getOrderConsigneeContacts", payload.orderNumber));
  }

  @Action
  saveOrderShortcutAction() {
    const currentOrder = this.currentOrder;
    const payload = buildOrderSavePayload(currentOrder);

    this.context.dispatch("system/updateIsSaving", true, { root: true });

    return api
      .putAsync(`/orders/${this.currentOrder.number}`, payload)
      .then(() => this.context.dispatch("reloadOrderShortcutAction"))
      .then(() => notifications.success("Order saved", "Order successfully saved."))
      .catch(ex => notifications.error("Order not saved", "Failed to save order.", ex))
      .then(() => this.context.dispatch("loadUncompletedOrderAlerts"))
      .then(() => {
        if (this.uncompletedOrderAlerts) {
          return this.uncompletedOrderAlerts;
        }
      })
      .catch(ex =>
        notifications.error(
          "Uncompleted orders not loaded",
          "Failed to load uncompleted orders.",
          ex
        )
      )
      .finally(() => this.context.dispatch("system/updateIsSaving", false, { root: true }));
  }

  @Action
  copyOrder(payload) {
    return api.postAsync(`/orders/${payload.orderNumber}/copy`, payload);
  }

  @Action
  getEmailCustomerSettingsForOrder(orderNumber) {
    return api.getAsync(`/orders/${orderNumber}/emailConsignee`);
  }

  @Action
  getTextCustomerSettingsForOrder(orderNumber) {
    return api.getAsync(`/orders/${orderNumber}/textConsignee`);
  }

  @Action
  sendConsigneeEmail(payload) {
    return api.postAsync(`/orders/${payload.orderNumber}/emailConsignee`, payload);
  }

  @Action
  sendConsigneeText(payload) {
    return api.postAsync(`/orders/${payload.orderNumber}/textConsignee`, payload);
  }

  @Action
  getVisionOpsLogsForOrder(orderNumber) {
    return api.getAsync(`/orders/${orderNumber}/visionOpsLogs`);
  }

  @Action
  getExpediteAuditLogsForOrder(orderNumber) {
    return api.getAsync(`/orders/${orderNumber}/expediteAudit`);
  }

  @Action
  getIntegrationLogsForOrder(orderNumber) {
    return api.getAsync(`/orders/${orderNumber}/integrationLogs`);
  }

  @Action
  applyOrderListSort(sort: string) {
    this.context.commit("setOrderSort", sort);
    this.context.dispatch("reloadOrders");
  }

  @Action
  applySearchSort(sort: string) {
    this.context.commit("setOrderSort", sort);
    this.context.dispatch("reloadAdvancedSearchResults", { page: 1, quickSearch: true });
  }

  @Action
  applyAdvancedSearchSort(sort: string) {
    this.context.commit("setOrderSort", sort);
    this.context.dispatch("reloadAdvancedSearchResults", { page: 1, quickSearch: false });
  }

  @Action
  async getOrderShipsWithOrdersSuggestions(orderNumber: number) {
    const response = await rawApi.get<OrderShipsWithSuggestionsResponse>(
      `/orders/${orderNumber}/shipsWithOrderSuggestions`
    );
    return response.data;
  }

  @Action
  async addOrderToShipsWithOrder(request: AddOrderToOrderShipsWithRequest) {
    const response = await rawApi.post<AddOrderToOrderShipsWithResponse>(
      `/orders/${request.childOrderNumber}/shipsWithOrder`,
      request
    );
    return response.data;
  }

  @Action
  updateAddressVerification(payload) {
    return api.postAsync(`/orders/${payload.orderNumber}/addressVerification/${payload.addressVerificationStatusId}`, {});
  }

  @Action
  async resetShipsWithOrder(orderNumber: number) {
    const response = await rawApi.post<ResetShipsWithOrderResponse>(
      `/orders/${orderNumber}/resetShipsWithOrder`,
      {}
    );
    return response.data;
  }

  @Action
  async updateCustomerNotReady(request: CustomerNotReadyRequest) {
    const response = await rawApi.post(
      `/orders/${request.headerNumber}/updateCustomerNotReady`,
      request
    );
    return response.data;
  }

  @Action
  async updateOrderRemarks(request: OrderRemarksRequest) {
    const response = await rawApi.post(`/orders/${request.headerNumber}/remarks`, request);
    return response.data;
  }

  @Action
  async updateOrderDriverNote(request: OrderDriverNoteRequest) {
    const response = await rawApi.post(`/orders/${request.headerNumber}/driverNote`, request);
    return response.data;
  }

  @Action
  async createNonCdsOrder(request: CreateNonCdsOrderRequest) {
    const response = await rawApi.post<CreateNonCdsOrderResponse>("/nonCdsOrders", request);
    return response.data;
  }

  @Action
  async updateNonCdsOrder(request: UpdateNonCdsOrderRequest) {
    const response = await rawApi.put<UpdateNonCdsOrderResponse>(
      `/nonCdsOrders/${request.id}`,
      request
    );
    return response.data;
  }

  @Action
  async getNonCdsOrder(orderNumber: number) {
    const response = await rawApi.get<NonCdsOrderResource>(`/nonCdsOrders/${orderNumber}`);
    return response.data;
  }

  @Action
  async getNonCdsOrderCommodities(orderNumber: number) {
    const response = await rawApi.get<NonCdsOrderCommodityResource[]>(
      `/nonCdsOrders/${orderNumber}/commodities`
    );
    return response.data;
  }

  @Action
  async addNonCdsOrderCommodity(request: AddNonCdsOrderCommodityRequest) {
    const response = await rawApi.post<AddNonCdsOrderCommodityResponse>(
      `/nonCdsOrders/${request.nonCDSOrderId}/commodities`,
      request
    );
    return response.data;
  }

  @Action
  async updateNonCdsOrderCommodity(request: UpdateNonCdsOrderCommodityRequest) {
    const response = await rawApi.put<UpdateNonCdsOrderRequest>(
      `/nonCdsOrders/${request.nonCDSOrderId}/commodities/${request.id}`,
      request
    );
    return response.data;
  }

  @Action
  async removeNonCdsOrderCommodity(request: RemoveNonCdsOrderCommodityRequest) {
    await rawApi.delete(`/nonCdsOrders/${request.orderId}/commodities/${request.commodityId}`);
  }

  @Action
  async addOrderImages(request: AddOrderImageRequest) {
    var formData = new FormData();
    request.files.forEach((file,i) => {
        formData.append("files", file);
    });
    formData.append("orderHeaderNumber", `${request.orderHeaderNumber}`);
    formData.append("imageType", request.imageType);

    const response = await rawApi.post<AddOrderImageResource>(
      `/orders/${request.orderHeaderNumber}/images`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" }});

    return response.data;
  }

  @Action
  async addDocumentBatch(request) {
    var formData = new FormData();
    request.files.forEach((file,i) => {
        formData.append("files", file);
    });
    formData.append("mountId", `${request.mountId}`);
    formData.append("facilityCode", request.facilityCode);

    const response = await rawApi.post('/images/batch',
      formData,
      { headers: { "Content-Type": "multipart/form-data" }});

    return response.data;
  }

  @Action
  async loadOrderThumbnails(order: any) {
    try {
      const response = await rawApi.get<OrderImageResource[]>(
        `/orders/${order.number}/images/thumbnails`
      );
      this.context.commit("setOrderThumbnails", response.data);
    } catch (error) {
      this.context.commit("setOrderThumbnails", { thumbnails: [] });
      throw error;
    }
  }

  @Action
  async loadOrderImages(order: any) {
    const response = await rawApi.get<OrderImageResource[]>(`/orders/${order.number}/images`);
    this.context.commit("setOrderImages", response.data);
  }

  @Action
  async getBulkOrderTimeframes(request: GetBulkOrderTimeframesRequest) {
    let url = "/orders/timeframes?";
    request.orderNumbers.forEach(orderNumber => {
      url += `ids=${orderNumber}&`;
    });
    const response = await rawApi.get<BulkOrderTimeframeResource[]>(url);
    return response.data;
  }

  @Action
  async setBulkOrderTimeframes(request: SetBulkOrderTimeframesRequest) {
    let url = "/orders/timeframes";
    const response = await rawApi.put<SetBulkOrderTimeframeResult[]>(url, request);
    return response.data;
  }

  @Action
  async setBulkOrderWantsAsap(request: SetBulkOrderWantsAsapRequest) {
    let url = "/orders/wantsasap";
    const response = await rawApi.put<SetBulkOrderWantsAsapResult[]>(url, request);
    return response.data;
  }

  @Action
  async allowFutureOrdersDeliveryType(deliveryType: string) {
    const response = await rawApi.get<AllowFutureOrdersResponse>(`/orders/${deliveryType}/allowFutureOrders`);
    return response.data;
  }

  @Action
  async previewCompleteOrder(orderNumber: number) {
    const response = await rawApi.get<PreviewCompleteResource>(`/orders/${orderNumber}/complete`);
    return response.data;
  }

  @Action
  async completeOrder(request: CompleteOrderRequest) {
    const response = await rawApi.post<CompleteOrderResponse>(
      `/orders/${request.orderHeaderNumber}/complete`,
      request
    );
    return response.data;
  }

  @Action
  async updateOrdersAfterAction(items: any) {
    for (let index = 0; index < items.length; index++) {
      const response = await rawApi.get<OrderListItemResource[]>(
        `/orders?OrderNumber=${items[index].headerNumber || items[index].number}`
      );

      if (response.data && response.data.length > 0)
        this.context.commit("updateOrderInList", response.data[0]);

      if (
        this.currentOrder &&
        (items[index].headerNumber === this.currentOrder.headerNumber ||
          items[index].number === this.currentOrder.number)
      )
        await this.context.dispatch("loadOrder", items[0].headerNumber || items[0].number);
    }
  }

  @Action
  async removeContactFromOrder(request: RemoveConsigneeContactFromOrderRequest) {
    try {
      await rawApi.delete(
        `/orders/${request.orderHeaderNumber}/consigneeContacts/${request.contactId}`
      );
      this.context.commit("removeContactFromList", request.contactId);
    } catch (ex) {
      throw ex;
    }
  }

  @Action
  async sendAccessCode(request: SendAccessCodeToContact) {
    try {
      await rawApi.post(
        `/CustomerPortal/SendAccessCode`, request
      );
      await this.getOrderConsigneeContacts(request.orderHeaderNumber);
    } catch (ex) {
      throw ex;
    }
  }


  @Action
  async loadAlertTypes() {
    try {
      const response = await rawApi.get<AlertTypeResource[]>(`/orderAlerts/types`);
      this.context.commit("setAlertTypes", response.data);
    } catch (ex) {
      console.error("Failed to load alert types");
    }
  }

  @Action
  async getBarcodeTransactions(request: GetBarcodeTransactionsRequest) {
    const response = await rawApi.get<BarcodeTransactionResource[]>(
      `/orders/${request.orderNumber}/barcode/${request.barcodeId}/transactions`
    );
    return response.data;
  }

  @Action
  async saveAccessorial(request: SaveAccessorialRequest) {
    let response = null;
    if (request.id) {
      response = await rawApi.put<SaveAccessorialResponse>(
        `/orders/${request.orderNumber}/accessorials/${request.id}`,
        request
      );
    } else {
      response = await rawApi.post<SaveAccessorialResponse>(
        `/orders/${request.orderNumber}/accessorials`,
        request
      );
    }
    return response.data;
  }

  @Action
  async deleteAccessorial(request: DeleteAccessorialRequest) {
    await rawApi.delete(`/orders/${request.orderNumber}/accessorials/${request.id}`);
  }

  @Action
  async addOrderPallet(request: AddOrderPalletRequest) {
    let response = null;
    response = await rawApi.post<AddOrderCommodityRequest>(
      `/orders/${request.orderHeaderNumber}/orderPallet`,
      request
    );
    return response.data;
  }

  @Action
  async previewScheduleOrder(request: PreviewScheduleOrderRequest) {
    let query: string = "";

    request.orderNumbers.forEach(orderNumber => {
      query += `OrderHeaderNumbers=${orderNumber}&`;
    });
    const response = await rawApi.get<PreviewScheduleOrderResource[]>(`/orders/schedule?${query}`);
    return response.data;
  }

  @Action
  async previewReceiveOrder(request: PreviewReceiveOrderRequest) {
    let query: string = "";

    request.orderNumbers.forEach(orderNumber => {
      query += `OrderHeaderNumbers=${orderNumber}&`;
    });
    const response = await rawApi.get<PreviewReceiveOrderResource[]>(`/orders/receive?${query}`);

    return response.data;
  }

  @Action
  addModalOrder(payload) {
    this.context.dispatch("system/addModalOrder", payload, { root: true });
  }

  @Action
  async getDeliveryExportFormats(): Promise<DeliveryExportFormat[]> {
    const response = await rawApi.get<DeliveryExportFormat[]>(`/deliveryExportFormats`);

    return response.data;
  }

  @Action
  deliveryExportModalShortcutAction() {
    return this.context.dispatch(
      "system/showModal",
      { name: modals.deliveryExportModal, data: null },
      { root: true }
    );
  }

  @Action
  completedOrderExportModalShortcutAction() {
    return this.context.dispatch(
      "system/showModal",
      { name: modals.completedOrderExportModal, data: null },
      { root: true }
    );
  }

  @Action
  documentUploadModalShortcutAction() {
    return this.context.dispatch(
      "system/showModal",
      { name: modals.documentUploadModal, data: null },
      { root: true }
    );
  }

  @Action
  async getOrderImageBundle(payload) {
    const response = await rawApi.post(`/orders/${payload.orderNumber}/images/pdf`, payload, {
      responseType: "blob"
    });
    return response.data;
  }

  @Action
  async getOrderLoadFeedbackFormExists(payload) {
    const response = await rawApi.get(`/orders/${payload.orderNumber}/loadFeedbackFormExists`);
    if (response.status === 200) {
      return response.data.loadFeedbackFormExists;
    }
    return false;
  }


  @Action
  async getOrderLoadFeedbackForm(payload) {
    try {
      const response = await rawApi.post(`/orders/${payload.orderNumber}/images/lff`, payload, {
        responseType: "blob"
      });

      return response.data;
    }
    catch (error) {
      if (error.response && error.response.status === 404) {
        return null;
      }
      throw error;
    }
  }

  @Action
  async removeOrderDocuments(payload) {
    return await rawApi
      .delete(`/orders/${payload.orderNumber}/images`, {
        data: { DocumentIds: payload.documentIds }
      })
      .then(response => {
        if (response.data.successful)
        {
          notifications.success("Document(s) deleted", "Document(s) successfully deleted.");
        } else {
          notifications.error("Unable to delete", "Unable to delete documents.");
        }

        return response;
      })
      .catch(ex => {
        notifications.error("Unable to delete", "Unable to delete one or more documents.", ex);
        return ex;
      });
  }

  @Action
  async reassignOrderDocuments(payload) {
    return await rawApi
      .post(`/orders/${payload.targetOrderNumber}/images/reassign`, payload)
      .then(response => {
        if (response.data.successful)
        {
          notifications.success("Document(s) reassigned", "Document(s) successfully reassigned.");
        } else {
          notifications.error("Unable to reassign", "Unable to reassign documents from legacy imaging systems.");
        }

        return response;
      })
      .catch(ex => {
        notifications.error("Unable to reassign", "Unable to reassign one or more documents.", ex);
        return ex;
      });
  }

  @Action
  async getImage(payload) {
    const response = await rawApi.get(`/images/${payload.source}/${payload.id}/${payload.page}`);
    return response.data;
  }

  @Action
  getOrderDeliveryHistoryLog(orderNumber) {
    return api.getAsync(`/orders/${orderNumber}/deliveryHistoryLog`);
  }

  @Action
  async getCurrentDeliveryProfile(orderNumber) {
    await api.getAsync(`/scheduling/${orderNumber}`).then(res => {
      this.context.commit("setCurrentDeliveryProfile", res.data);
    });
  }

  @Action
  async getSchedulingReasonCodeLegend(billToId: string) {
    const response = await rawApi.get<{ hasReasonCodes: boolean; legend: { code: string; description: string }[] }>(`/orders/schedulingReasonCodeLegend/${billToId}`);
    return response.data;
  }

  @Action
  async geoCodeAddress(payload : any) {
    const result = await api.postAsync(`/geocode/${payload.contact.id}/${payload.orderHeader}`, payload.contact.address);

    if(payload.type === "shipper") {
      this.setShipperLocation(result.data.location);
    } else {
      this.setConsigneeLocation(result.data.location);
    }

    return result;
  }

  @Mutation
  setConsigneeLocation(model : any) {
    if(this.currentOrder.consignee.location === null || this.currentOrder.consignee.location === undefined) {
      this.currentOrder.consignee.location = {
        isZipCodeLocation: false,
        latitude: 0,
        longitude: 0
      }
    }

    this.currentOrder.consignee.location.latitude = parseFloat(model.latitude);
    this.currentOrder.consignee.location.longitude = parseFloat(model.longitude);
  }

  @Mutation
  setShipperLocation(model : any) {
    if(this.currentOrder.shipper.location === null || this.currentOrder.shipper.location === undefined) {
      this.currentOrder.shipper.location = {
        isZipCodeLocation: false,
        latitude: 0,
        longitude: 0
      }
    }

    this.currentOrder.shipper.location.latitude = parseFloat(model.latitude);
    this.currentOrder.shipper.location.longitude = parseFloat(model.longitude);
  }

  @Action
  async getZipCodeCoordinates(model : any) {
    const result = await api.getAsync('/geocode/zip-code/' + model.zipCode);
    if(model.type === "shipper") {
      this.setShipperLocation(result.data.location);
    } else {
      this.setConsigneeLocation(result.data.location);
    }
  }

  @Action
  async updateCompanyCoordinates(model : any) {
    const passModel = {
      latitude: parseFloat(model.latitude),
      longitude: parseFloat(model.longitude),
      id: model.id,
      orderHeader: model.orderHeader
    }

    const result =  await api.postAsync(`/geocode/update-company`, passModel);

    if(model.type === "shipper") {
      this.setShipperLocation(model);
    } else {
      this.setConsigneeLocation(model);
    }

    return result;
  }
}
